/**
 * Types for the GetAgentWork operation. Mirrors the Coral/Smithy model:
 *
 *   operation GetAgentWork {
 *     input  := { @required auditId, @required taskId, runId }
 *     output := { @required steps: AgentWorkStepList }
 *   }
 */

export interface AgentWorkStep {
  stepName: string;
  status: string;
  inputRequest?: string;
  artifacts?: string;
  inputData?: string;
  attemptNumber?: number;
  updatedAt?: string;
}

export interface GetAgentWorkRequest {
  auditId: string;
  taskId: string;
  /** Optional: resume a specific run; omitted returns the latest run. */
  runId?: string;
}

export interface GetAgentWorkResponse {
  steps: AgentWorkStep[];
}

// ---------------------------------------------------------------------------
// Input request payload
//
// A step's `inputRequest` is a JSON string. Once parsed it has this shape; the
// `fields` drive the generative form the parsing component renders.
// ---------------------------------------------------------------------------

export type AgentWorkFieldType =
  | "single_select"
  | "multi_select"
  | "text"
  | "drop_down"
  | "date_range"
  | "number"
  | "file_upload";

export interface AgentWorkOption {
  value: string;
  /** Whether the option is pre-selected when the form first renders. */
  isSelected?: boolean;
  /** Whether the option is mandatory (cannot be deselected). */
  isRequired?: boolean;
}

export interface AgentWorkField {
  name: string;
  label: string;
  type: AgentWorkFieldType;
  required?: boolean;
  options?: AgentWorkOption[];
}

export interface AgentWorkInputRequest {
  prompt: string;
  inputType: string;
  fields: AgentWorkField[];
}

// ---------------------------------------------------------------------------
// InvokeAgent operation (POST /audit-agent/invoke)
//
// Submits the user's response back to the agent to start or continue a run.
// ---------------------------------------------------------------------------

export enum AgentTaskType {
  SAMPLING = "SAMPLING",
}

// Temporary prompt will be updated in the future
export const SAMPLING_PROMPT = "start sampling";

export enum AgentInputType {
  DATA = "DATA",
  FILE = "FILE",
}

/** A single piece of input. DATA content is a JSON string of field values; FILE
 * content is a base64 file payload when submitted in an InvokeAgent POST, and
 * an uploaded file's S3 key when returned as a completed step's GET
 * `inputData`. */
export interface AgentInvokeInput {
  type: AgentInputType;
  content: string;
}

export interface InvokeAgentRequest {
  prompt: string;
  isNewRun: boolean;
  auditId: string;
  taskId: string;
  taskType?: AgentTaskType;
  input?: AgentInvokeInput[];
  /** The run to start. Temporarily hardcoded by the "New run" button until the
   * run history API provides real run ids. */
  runId?: string;
}

export interface InvokeAgentResponse {
  sessionId: string;
  runId?: string;
  status: string;
}

// ---------------------------------------------------------------------------
// File download
//
// A completed step's FILE inputData entries hold an S3 key. To download one we
// exchange the key for a short-lived presigned URL.
// ---------------------------------------------------------------------------

export interface AgentFileDownloadRequest {
  s3Key: string;
  teamName: string;
}

export interface AgentFileDownloadResponse {
  downloadUrl: string;
}

/** A file a step produced. A step's `artifacts` is a JSON string array of
 * these; `type` doubles as the download button label. */
export interface AgentArtifact {
  type: string;
  s3Key: string;
}

// ---------------------------------------------------------------------------
// Run history
//
// A task can be worked by the agent across multiple runs. The run history
// lists every run for a task so the user can inspect past attempts.
//
// NOTE: The backend operation does not exist yet — the endpoint currently
// returns mock data and the request/response shapes below are provisional and
// likely to change once the API is defined.
// ---------------------------------------------------------------------------

/** A single agent run for a task. */
export interface AgentRunHistoryEntry {
  runId: string;
  /** Run lifecycle status, reusing the AgentStatus values. */
  status: string;
  /** ISO-8601 timestamp of when the run started. */
  startedAt: string;
  /** ISO-8601 timestamp of the run's last update. */
  updatedAt: string;
  /** User who triggered the run, if known. */
  triggeredBy?: string;
}

export interface GetAgentRunHistoryRequest {
  auditId: string;
  taskId: string;
}

export interface GetAgentRunHistoryResponse {
  runs: AgentRunHistoryEntry[];
}
